import type { AnalyticsObject, AnalyticsProperties } from '@core/analytics/types'
import { NA } from '@core/analytics/helpers/non-available'
import { type Competitiveness, getCompetitiveness } from '../competitiveness'
import type { GroupedQuotes, Tab } from '../../types'
import { isRecommendedProvider } from '../../helpers/recommended'
import { getBestTransferTime } from '../transfer-time'
import type { PaymentType } from '@core/types/imt'
import { totalCost, totalStandardCost } from '../../helpers/pricing'

export type ComparisonSectionData = {
  tab: Tab
  ranking: number
  language: string
  comparisonId: string
  amount: number
  amountUSD: number
  midMarketRate: number
  providersInUserList: string[]
}

export class ComparisonSection implements AnalyticsObject {
  name: string
  properties: ComparisonSectionProperties
  constructor(properties: ComparisonSectionProperties) {
    this.name = 'IMT Comparison Section'
    this.properties = properties
  }

  static getInstance({ tab, ranking, language, comparisonId, ...data }: ComparisonSectionData) {
    const spotlight = tab.items.find((r) => r.label)
    const cheapestProvider = tab.items.find((i) => i.bestDeal)

    return new ComparisonSection({
      comparisonSection: {
        comparisonId,
        ranking,
        type: sectionType(tab.type),
        resultsQuality: tab.items.some((gq) => isRecommendedProvider(gq.psp)) ? 'good' : 'poor',
        nbResults: tab.items.length,
        cheapestProvider: cheapestProvider?.psp.name ?? NA,
        cheapestProviderExposureIndex: cheapestProvider?.exposure ?? NA,
        cheapestProviderTotalCostStandardPricing: cheapestProvider
          ? totalStandardCost(
              data.amount,
              data.midMarketRate,
              cheapestProvider.cheapestService.quote,
            )
          : undefined,
        cheapestProviderTotalCost: cheapestProvider
          ? totalCost(data.amount, data.midMarketRate, cheapestProvider.cheapestService.quote)
          : undefined,
        cheapestProviderStandardPricing: cheapestStandardProvider(tab.items),
        fastestProvider: fastestProvider(tab.items),
        highestMonitoScoreProvider: highestScoreProvider(tab.items),
        firstProviderIsAffiliate: Boolean(tab.items[0]?.psp?.affiliate),
        firstProviderCompetitiveness: getCompetitiveness(
          totalCost(data.amount, data.midMarketRate, tab.items[0].cheapestService.quote),
          data.amountUSD,
        ),
        listProvidersAvailable: tab.items.map((item) => item.psp.name),
        listProvidersVeryCheap: competitiveProviders(tab.items, data, 'Very Cheap'),
        listProvidersCheap: competitiveProviders(tab.items, data, 'Cheap'),
        listProvidersAverage: competitiveProviders(tab.items, data, 'Average'),
        listProvidersExpensive: competitiveProviders(tab.items, data, 'Expensive'),
        listProvidersVeryExpensive: competitiveProviders(tab.items, data, 'Very Expensive'),
        listProvidersVeryCheapStandardPricing: competitiveProvidersStandardCost(
          tab.items,
          data,
          'Very Cheap',
        ),
        listProvidersCheapStandardPricing: competitiveProvidersStandardCost(
          tab.items,
          data,
          'Cheap',
        ),
        listProvidersLanguagesNotMatching: getProvidersLanguagesNotMatching(tab.items, language),
        nbServicesDisplayed: tab.items.reduce((sum, i) => sum + i.services.length, 0),
        medianExposureIndex: medianExposureIndex(tab.items),
        firstProviderExposureIndex: tab.items[0]?.exposure ?? undefined,
        spotlightProvider: spotlight?.psp.name || NA,
        spotlightLabel: spotlight?.label || NA,
        spotlightExposureIndex: spotlight?.exposure ?? NA,
        providersInUserList: data.providersInUserList,
      },
    })
  }
}

export interface ComparisonSectionProperties extends AnalyticsProperties {
  comparisonSection: {
    comparisonId: string
    type: ComparisonSectionType | undefined
    nbResults: number
    ranking: number | undefined
    cheapestProvider: string
    cheapestProviderExposureIndex: number | typeof NA
    cheapestProviderStandardPricing: string
    cheapestProviderTotalCostStandardPricing: number | undefined
    cheapestProviderTotalCost: number | undefined
    fastestProvider: string
    highestMonitoScoreProvider: string
    firstProviderIsAffiliate: boolean
    firstProviderCompetitiveness: Competitiveness
    listProvidersAvailable: string[]
    listProvidersVeryCheap: string[]
    listProvidersCheap: string[]
    listProvidersAverage: string[]
    listProvidersExpensive: string[]
    listProvidersVeryExpensive: string[]
    listProvidersVeryCheapStandardPricing: string[]
    listProvidersCheapStandardPricing: string[]
    listProvidersLanguagesNotMatching: string[]
    nbServicesDisplayed: number
    medianExposureIndex: number
    firstProviderExposureIndex: number | undefined
    spotlightProvider: (string & {}) | typeof NA
    spotlightLabel: (string & {}) | typeof NA
    spotlightExposureIndex: number | typeof NA
    resultsQuality: 'poor' | 'good'
    providersInUserList: string[]
  }
}

export type ComparisonSectionType =
  | 'Bank'
  | 'Cash'
  | 'FX Brokers'
  | 'Airtime Top-Up'
  | 'Mobile Wallet'
  | 'Cash Home Delivery'
  | 'prepaid_card'
  | 'partner_bank_account'

export const SECTIONS_MAP: Record<PaymentType, ComparisonSectionType> = {
  bank: 'Bank',
  cash: 'Cash',
  broker: 'FX Brokers',
  topup: 'Airtime Top-Up',
  wallet: 'Mobile Wallet',
  delivery: 'Cash Home Delivery',
  card: 'prepaid_card',
} as const

function sectionType(type: PaymentType) {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return SECTIONS_MAP[type] || [`Not mapped:${type}`].join(':')
}

export function getProvidersLanguagesNotMatching(results: GroupedQuotes[], language: string) {
  return results.reduce((acc: string[], result) => {
    if (!result.psp.languages.some((l) => l.code === language)) {
      acc.push(result.psp.name)
    }
    return acc
  }, [])
}

function cheapestStandardProvider(results: GroupedQuotes[]) {
  let bestAmount = 0
  let cheapest = NA
  for (const result of results) {
    if (result.cheapestService.quote.receivedAmount > bestAmount) {
      bestAmount = result.cheapestService.quote.receivedAmount
      cheapest = result.psp.name
    }
  }
  return cheapest
}

function fastestProvider(results: GroupedQuotes[]) {
  let bestTime = Number.MAX_SAFE_INTEGER
  let best = NA
  for (const result of results) {
    const transferTime = getBestTransferTime(result)
    if (!transferTime) continue
    const time = (transferTime.min + transferTime.max) / 2
    if (time < bestTime) {
      bestTime = time
      best = result.psp.name
    }
  }
  return best
}

function highestScoreProvider(results: GroupedQuotes[]) {
  let maxScore = 0
  let bestRated = NA
  for (const { psp } of results) {
    const score = psp.score?.value
    if (score && score > maxScore) {
      maxScore = score
      bestRated = psp.name
    }
  }
  return bestRated
}

function competitiveProviders(
  results: GroupedQuotes[],
  {
    amount,
    midMarketRate,
    amountUSD,
  }: { amount: number; amountUSD: number; midMarketRate: number },
  level: Competitiveness,
) {
  return results
    .filter((item) => {
      const quote = item.cheapestService.quote
      return getCompetitiveness(totalCost(amount, midMarketRate, quote), amountUSD) === level
    })
    .map((r) => r.psp.name)
}

function competitiveProvidersStandardCost(
  results: GroupedQuotes[],
  {
    amount,
    midMarketRate,
    amountUSD,
  }: { amount: number; amountUSD: number; midMarketRate: number },
  level: Competitiveness,
) {
  return results
    .filter((item) => {
      const quote = item.cheapestService.quote
      return (
        getCompetitiveness(totalStandardCost(amount, midMarketRate, quote), amountUSD) === level
      )
    })
    .map((r) => r.psp.name)
}

export function medianExposureIndex(results: GroupedQuotes[]) {
  return median(results.map((r) => r.exposure ?? 0))
}
